/**
 * The cards and the table this account deals with (D87).
 *
 * One component, two places, and that is the point: it is on the arrival
 * screen where a host is already saying who they are, and behind the table
 * menu so they can change their mind without leaving the game. It used to live
 * on the Account page, which is the one screen you cannot reach while playing.
 *
 * ## Why only a host sees it
 *
 * The deck and the table belong to the ROOM and come from the host, the way
 * the host of a real game supplies the pack. A guest who changed theirs would
 * be changing nothing they could see - the table repaints from the host's ids
 * and nobody else's - so offering it to them is a control that lies. They pick
 * theirs when they are the one hosting.
 *
 * ## Owning and wearing are still different things
 *
 * Nothing here can offer what the account does not own: the equip RPC would
 * refuse it, and a picker that promises otherwise is the reason this screen
 * stayed empty until the server could answer. Deck and table are picked apart
 * because they are separate ids that may come from separate purchases - a
 * theme sells them together and nothing says you have to wear them together.
 */

import { useState } from 'react';
import { equipRpc } from '../net/session.js';
import type { CatalogTheme, Equipped } from '../net/session.js';
import { Icon } from '../render/Icon.js';
import { useSession } from '../session-context.js';
import { useCatalog } from '../use-catalog.js';
import { describeError } from './errors.js';
import { ThemePicker } from './ThemePicker.js';
import type { ThemeChoice } from './ThemePicker.js';

export function Wardrobe({
  onChanged,
}: {
  /**
   * Told after the server has accepted a change.
   *
   * The table repaints from the host's ids, and the match caches those when
   * somebody joins - so a change made mid-game reaches nobody until the match
   * is told to look again. That is what this is for; see `LOOK` in the
   * protocol.
   */
  onChanged?: (() => void) | undefined;
}) {
  const { session } = useSession();
  const { catalog, refresh } = useCatalog();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [equipped, setEquipped] = useState<Equipped | null>(null);

  const worn = equipped ?? catalog?.equipped ?? null;
  if (!catalog || !session || worn === null) return null;

  const owned = catalog.themes.filter((theme) => theme.owned);
  /* One theme owned is the free one, and "choose between your one thing" is
     not a choice. The store is where the second one comes from. */
  if (owned.length < 2) return null;

  const wear = async (wanted: { deckId?: string; tableId?: string }) => {
    if (busy) return;
    setBusy(true);
    setFailed(null);
    try {
      const result = await equipRpc(session, wanted);
      if (!result.ok || !result.equipped) {
        setFailed(result.message ?? 'That could not be worn.');
        return;
      }
      setEquipped(result.equipped);
      await refresh();
      onChanged?.();
    } catch (caught) {
      setFailed(describeError(caught, 'Could not reach the server.'));
    } finally {
      setBusy(false);
    }
  };

  const choices = (idOf: (theme: CatalogTheme) => string): ThemeChoice[] =>
    owned.map((theme) => ({
      id: idOf(theme),
      name: theme.name,
      /* Both, because the two pickers draw different things: one shows cards
         and one shows the room. Named separately because Classic's table is
         "default" while its deck is "classic". */
      deckId: theme.contents.deckId,
      tableId: theme.contents.tableId,
    }));

  return (
    <>
      {failed ? <p className="banner error">{failed}</p> : null}
      <ThemePicker
        label="Cards"
        help="The deck everyone at your table plays with."
        preview="cards"
        options={choices((theme) => theme.contents.deckId)}
        current={worn.deckId}
        busy={busy}
        onPick={(deckId) => void wear({ deckId })}
      />
      <ThemePicker
        label="Table"
        help="The table they play on."
        preview="table"
        options={choices((theme) => theme.contents.tableId)}
        current={worn.tableId}
        busy={busy}
        onPick={(tableId) => void wear({ tableId })}
      />
    </>
  );
}

/**
 * The wardrobe as a sheet over the table.
 *
 * A sibling of the menu rather than a child of it, which is the pattern How to
 * play and Chat already use: the row closes the menu and opens this. That is
 * not tidiness - it is the only arrangement where you can SEE what you are
 * choosing. Stacked on top of the menu, the thing behind this sheet was a list
 * of room settings; opened in its place, it is the table, and pressing
 * Medieval repaints the cards under your thumb.
 *
 * Dimmed rather than solid for the same reason. The menu is a screen you read
 * instead of the table; this one asks a question about it.
 */
export function WardrobeSheet({
  onChanged,
  onClose,
}: {
  onChanged: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className="sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Cards and table"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet-body">
        <div className="sheet-head">
          <p className="sheet-title">Cards and table</p>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        <p className="menu-note">Everyone you invite plays on these.</p>
        <Wardrobe onChanged={onChanged} />
      </div>
    </div>
  );
}
